import React, { useState, useEffect, useCallback } from 'react';
import { User, Affiliate } from '../../types';
import { adminService, InstallAllowlistState, MissingDesktopReport } from '../../services/adminService';
import { authService } from '../../services/authService';
import { useToast } from '../../contexts/ToastContext';
import { ConfirmDialog } from '../ConfirmDialog';
import { TableSkeleton } from '../Skeleton';
import { RefreshCw, Plus, Trash2, ShieldCheck, Network, Monitor } from 'lucide-react';
import { ADMIN_LIST_CAPPED } from './adminLayout';

interface InstallDeployManagerProps {
  currentUser: User;
}

/**
 * 전사 배포 탭 — 두 섹션:
 *  1) 설치 허용 IP: 사내망 기본 대역(읽기) + 관리자가 더하는 예외 IP/CIDR(추가·삭제). super_admin 전용.
 *     배포 패키지의 deploy-inatalk.cmd가 설치 직전 GET /api/updates/install-allowed로 이 판정을 묻는다.
 *  2) PC 앱 미설치 현황: 승인 직원 중 device_tokens 기록이 없는(= PC 앱 미설치) 목록 + 설치율.
 */
export const InstallDeployManager: React.FC<InstallDeployManagerProps> = ({ currentUser }) => {
  const { showToast } = useToast();

  // ── 섹션 1: 설치 허용 IP ──
  const [allowlist, setAllowlist] = useState<InstallAllowlistState | null>(null);
  const [alLoading, setAlLoading] = useState(false);
  const [alError, setAlError] = useState<string | null>(null);
  const [newValue, setNewValue] = useState('');
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);

  const loadAllowlist = useCallback(async () => {
    setAlLoading(true);
    setAlError(null);
    try {
      setAllowlist(await adminService.getInstallAllowlist());
    } catch (err) {
      setAlError(err instanceof Error ? err.message : '설치 허용 IP를 불러오지 못했습니다.');
    } finally {
      setAlLoading(false);
    }
  }, []);

  const handleAdd = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const value = newValue.trim();
    if (!value || adding) return;
    setAdding(true);
    try {
      await adminService.addInstallAllowlist(value, newNote.trim() || undefined);
      setNewValue('');
      setNewNote('');
      showToast({ type: 'success', message: '설치 허용 IP를 추가했습니다.' });
      await loadAllowlist();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : '추가하지 못했습니다.' });
    } finally {
      setAdding(false);
    }
  }, [newValue, newNote, adding, showToast, loadAllowlist]);

  const handleRemove = useCallback(async (value: string) => {
    try {
      await adminService.removeInstallAllowlist(value);
      showToast({ type: 'success', message: '설치 허용 IP를 삭제했습니다.' });
      await loadAllowlist();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : '삭제하지 못했습니다.' });
    } finally {
      setRemoveTarget(null);
    }
  }, [showToast, loadAllowlist]);

  // ── 섹션 2: 미설치 PC 현황 ──
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [affiliateId, setAffiliateId] = useState<string>(currentUser.affiliateId || '');
  const [report, setReport] = useState<MissingDesktopReport | null>(null);
  const [repLoading, setRepLoading] = useState(false);
  const [repError, setRepError] = useState<string | null>(null);

  const loadReport = useCallback(async (aff: string) => {
    setRepLoading(true);
    setRepError(null);
    try {
      setReport(await adminService.getMissingDesktop(aff || undefined));
    } catch (err) {
      setRepError(err instanceof Error ? err.message : '미설치 PC 목록을 불러오지 못했습니다.');
    } finally {
      setRepLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllowlist();
    authService.getAffiliates().then(setAffiliates).catch(() => {});
  }, [loadAllowlist]);

  useEffect(() => {
    loadReport(affiliateId);
  }, [affiliateId, loadReport]);

  const installed = report ? report.approvedTotal - report.missing.length : 0;
  const rate = report && report.approvedTotal > 0
    ? Math.round((installed / report.approvedTotal) * 100)
    : 0;

  return (
    <div className="space-y-8">
      {/* ── 섹션 1: 설치 허용 IP ── */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">설치 허용 IP</h3>
          {allowlist && (
            <span className={`ml-1 px-2 py-0.5 text-xs rounded-full font-medium ${
              allowlist.gateEnabled ? 'bg-brand-50 text-brand-700' : 'bg-slate-100 text-slate-500'
            }`}>
              게이트 {allowlist.gateEnabled ? '켜짐' : '꺼짐'}
            </span>
          )}
          <button
            onClick={loadAllowlist}
            disabled={alLoading}
            className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={alLoading ? 'animate-spin' : ''} />
            새로고침
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          배포 설치본은 여기 등록된 대역에서만 설치됩니다. 사외로 가지고 나간 설치본은 사내망이 아니면 설치되지 않습니다.
        </p>

        {/* 기본 사내 대역 (읽기 전용 — .env INSTALL_ALLOWED_CIDRS) */}
        {allowlist && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Network size={13} /> 기본 사내 대역
            </span>
            {allowlist.baseCidrs.map(c => (
              <span key={c} className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-600 font-mono">{c}</span>
            ))}
          </div>
        )}

        {/* 예외 추가 폼 */}
        <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-2 mb-3">
          <div className="flex-1 min-w-[10rem]">
            <label className="block text-xs text-slate-500 mb-1">예외 IP 또는 대역</label>
            <input
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              placeholder="211.195.214.5 또는 10.0.0.0/8"
              className="w-full px-3 py-1.5 text-sm font-mono border border-slate-200 rounded-lg focus:outline-none focus:border-brand-400"
            />
          </div>
          <div className="flex-1 min-w-[8rem]">
            <label className="block text-xs text-slate-500 mb-1">메모 (선택)</label>
            <input
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="예: 2공장 회선"
              className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-brand-400"
            />
          </div>
          <button
            type="submit"
            disabled={adding || !newValue.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            <Plus size={15} /> 추가
          </button>
        </form>

        {alError && (
          <div className="mb-3 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded border border-brand-200">
            {alError}
          </div>
        )}

        {/* 예외 목록 */}
        {alLoading && !allowlist ? (
          <TableSkeleton rows={2} cols={2} />
        ) : allowlist && allowlist.entries.length === 0 ? (
          <div className="text-center py-6 text-sm text-slate-400">등록된 예외 IP가 없습니다</div>
        ) : (
          <div className={`space-y-2 ${ADMIN_LIST_CAPPED}`}>
            {allowlist?.entries.map(entry => (
              <div key={entry.value} className="flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-xl">
                <div className="min-w-0">
                  <span className="text-sm font-mono text-slate-800">{entry.value}</span>
                  {entry.note && <span className="ml-2 text-xs text-slate-500">{entry.note}</span>}
                  <div className="text-xs text-slate-400 truncate">
                    {entry.addedByName}{entry.addedAt ? ` · ${new Date(entry.addedAt).toLocaleString('ko-KR')}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => setRemoveTarget(entry.value)}
                  className="shrink-0 p-1.5 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                  aria-label={`${entry.value} 삭제`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── 섹션 2: PC 앱 미설치 현황 ── */}
      <section className="pt-6 border-t border-slate-200">
        <div className="flex items-center gap-2 mb-1">
          <Monitor size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">PC 앱 미설치 현황</h3>
          {affiliates.length > 1 && (
            <select
              value={affiliateId}
              onChange={e => setAffiliateId(e.target.value)}
              className="ml-1 px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-400"
            >
              {affiliates.map(a => (
                <option key={a.id} value={a.id}>{a.label || a.id}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => loadReport(affiliateId)}
            disabled={repLoading}
            className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={repLoading ? 'animate-spin' : ''} />
            새로고침
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          승인 직원 중 PC 앱으로 한 번도 로그인하지 않은 사람입니다. 이 목록을 기준으로 배포 대상을 좁힐 수 있습니다.
        </p>

        {report && (
          <div className="mb-3 flex items-center gap-3 text-sm">
            <span className="text-slate-700 font-medium">설치율 {rate}%</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">{installed} / {report.approvedTotal}명 설치</span>
            <span className="text-slate-400">·</span>
            <span className="text-brand-700">미설치 {report.missing.length}명</span>
          </div>
        )}

        {repError && (
          <div className="mb-3 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded border border-brand-200">
            {repError}
          </div>
        )}

        {repLoading && !report ? (
          <TableSkeleton rows={4} cols={2} />
        ) : report && report.missing.length === 0 && !repError ? (
          <div className="text-center py-6 text-sm text-slate-400">
            {report.approvedTotal === 0 ? '승인 명단이 비어 있습니다' : '모든 승인 직원이 PC 앱을 설치했습니다'}
          </div>
        ) : (
          <div className={`space-y-2 ${ADMIN_LIST_CAPPED}`}>
            {report?.missing.map(m => (
              <div key={m.userId} className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-xl">
                <div className="h-8 w-8 bg-slate-50 rounded-lg flex items-center justify-center shrink-0">
                  <Monitor size={15} className="text-slate-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{m.userName || '(이름 없음)'}</div>
                  <div className="text-xs text-slate-400 truncate">
                    사번: {m.userId}{m.depart ? ` · ${m.depart}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={removeTarget !== null}
        title="설치 허용 IP 삭제"
        message={`'${removeTarget}'을(를) 삭제할까요? 이 IP에서는 더 이상 설치본이 설치되지 않습니다.`}
        confirmLabel="삭제"
        danger
        onConfirm={() => removeTarget && handleRemove(removeTarget)}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};
